// Portal API service - no auth store needed (token-based access only)
// Relative path — proxied by rspack dev server (local) or nginx (production)
const API_BASE_URL = '/api';

interface RequestOptions {
	method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	body?: unknown;
}

interface ApiError {
	error?: { message: string };
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
	const { method = 'GET', body } = options;

	const headers: Record<string, string> = {
		'Content-Type': 'application/json'
	};

	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method,
		headers,
		body: body ? JSON.stringify(body) : undefined
	});

	const data = await response.json() as (T & ApiError);

	if (!response.ok) {
		throw new Error(data.error?.message || 'Request failed');
	}

	return data;
}

// Candidates (public, token-based)
export const candidateApi = {
	getJobInfo: (token: string) =>
		request<{ success: boolean; data: any }>(`/candidates/job-info/${token}`),

	verifyEmail: (token: string, email: string) =>
		request<{ success: boolean; data: any }>('/candidates/verify-email', {
			method: 'POST',
			body: { token, email }
		}),

	uploadCV: async (formData: FormData) => {
		const response = await fetch(`${API_BASE_URL}/candidates/upload-cv`, {
			method: 'POST',
			body: formData
		});
		const data: any = await response.json();
		if (!response.ok) throw new Error(data.error?.message || 'Upload failed');
		return data;
	}
};

// Candidate Portal (identity-match login, portalToken-based)

async function portalRequest<T>(endpoint: string, portalToken: string, options: RequestOptions = {}): Promise<T> {
	const { method = 'GET', body } = options;
	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		'x-portal-token': portalToken
	};
	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method,
		headers,
		body: body ? JSON.stringify(body) : undefined
	});
	const data = await response.json() as (T & ApiError);
	if (!response.ok) throw new Error(data.error?.message || 'Request failed');
	return data;
}

export const candidatePortalApi = {
	login: (identifier: string) =>
		request<{ success: boolean; data: { portalToken: string; candidate: any } }>('/candidate-portal/login', {
			method: 'POST',
			body: { identifier }
		}),

	getJobs: (portalToken: string) =>
		portalRequest<{ success: boolean; data: any[] }>('/candidate-portal/jobs', portalToken),

	getJobDetail: (portalToken: string, hiringRequestId: string) =>
		portalRequest<{ success: boolean; data: any }>(`/candidate-portal/jobs/${hiringRequestId}`, portalToken)
};

// Access Link public helpers
export const accessLinkApi = {
	validateToken: (token: string) =>
		request<{ success: boolean; data: any }>(`/access-links/validate/${token}`),

	verifyEmail: (token: string, email: string) =>
		request<{ success: boolean; data: any }>('/access-links/verify-email', {
			method: 'POST',
			body: { token, email }
		})
};

// HM Portal (public, token-based)
export const hmPortalApi = {
	getJobs: (token: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs?token=${token}`),

	getCandidates: (token: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/candidates?token=${token}`),

	addComment: (token: string, hiringRequestId: string, comment: string) =>
		request<{ success: boolean; data: any }>(`/hm-portal/jobs/${hiringRequestId}/comments?token=${token}`, {
			method: 'POST',
			body: { comment }
		}),

	getComments: (token: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/comments?token=${token}`),

	rateCandidate: (token: string, hiringRequestId: string, candidateId: string, rating: number, notes?: string) =>
		request<{ success: boolean; data: any }>(`/hm-portal/jobs/${hiringRequestId}/candidates/${candidateId}/rate?token=${token}`, {
			method: 'POST',
			body: { rating, notes }
		}),

	getRatings: (token: string, hiringRequestId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/ratings?token=${token}`),

	addFeedback: (token: string, hiringRequestId: string, candidateId: string, feedback: string) =>
		request<{ success: boolean; data: any }>(`/hm-portal/jobs/${hiringRequestId}/candidates/${candidateId}/feedback?token=${token}`, {
			method: 'POST',
			body: { feedback }
		}),

	getFeedback: (token: string, hiringRequestId: string, candidateId: string) =>
		request<{ success: boolean; data: any[] }>(`/hm-portal/jobs/${hiringRequestId}/candidates/${candidateId}/feedback?token=${token}`),

	// Download a generated CV / consultant-notes PDF. Uses fetch+blob through the same
	// proxied /api path as every other portal call (instead of window.open on a relative
	// URL, which resolved against the wrong origin and failed).
	downloadCVPDF: (token: string, cvId: string) =>
		fetch(`${API_BASE_URL}/hm-portal/cv/${cvId}/pdf?token=${encodeURIComponent(token)}`)
			.then(async res => {
				if (!res.ok) throw new Error('PDF download failed');
				const blob = await res.blob();
				const cd = res.headers.get('Content-Disposition') || '';
				const match = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
				const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
				return { blob, filename };
			})
};
